/**
 * Instruction Video Overlay
 * Used primarily instruction videos with no demos
 */

export function createInstructionVideoOverlay({
  videoUrl = null,
  commentImageUrl = null,
  commentImageTop = "0px",
  commentImageLeft = "50%",
  commentImageWidth = null,
  commentImageHeight = null,
  containerVerticalOffset = "0px",
  backgroundImageUrl = null,
  onContinue,
  forwardText = '[press "/" twice to continue ->]',
  backText = '[press "R" to replay]',
} = {}) {
  // Create the main overlay backdrop with blue background
  const overlay = document.createElement("div");
  overlay.id = "gear-instruction-video-overlay";
  overlay.style.position = "fixed";
  overlay.style.top = "0";
  overlay.style.left = "0";
  overlay.style.width = "100%";
  overlay.style.height = "100%";
  overlay.style.display = "flex";
  overlay.style.flexDirection = "column";
  overlay.style.alignItems = "center";
  overlay.style.justifyContent = "center";
  if (backgroundImageUrl) {
    overlay.style.backgroundImage = `url('${backgroundImageUrl}')`;
    overlay.style.backgroundSize = "cover";
    overlay.style.backgroundRepeat = "no-repeat";
    overlay.style.backgroundPosition = "center";
  } else {
    overlay.style.background = "#3da7c2";
  }
  overlay.style.zIndex = "10000";
  overlay.style.fontFamily = "sans-serif";

  // Create the main container with hologram frame
  const container = document.createElement("div");
  container.style.position = "relative";
  container.style.transform = `translateY(${containerVerticalOffset})`;
  container.style.backgroundImage = "url('assets/images/instructions/tutorial_tv_placeholder.png')";
  container.style.backgroundSize = "contain";
  container.style.backgroundRepeat = "no-repeat";
  container.style.backgroundPosition = "center";
  container.style.display = "flex";
  container.style.flexDirection = "column";
  container.style.alignItems = "center";
  container.style.justifyContent = "center";
  container.style.minWidth = "705px";
  container.style.minHeight = "560px";

  // The Video element
  let videoElement = null;
  let slashArmed = false;
  if (videoUrl) {
    // Create video wrapper for custom controls
    const videoWrapper = document.createElement("div");
    videoWrapper.style.position = "relative";
    videoWrapper.style.width = "416px";
    videoWrapper.style.height = "250px";
    videoWrapper.style.marginTop = "-26px";
    videoWrapper.style.marginBottom = "10px";
    videoWrapper.style.marginRight = "0px";

    // Create video element without native controls
    const video = document.createElement("video");
    video.src = videoUrl;
    video.controls = false;
    video.preload = "metadata";
    video.playsInline = true;
    video.style.width = "100%";
    video.style.height = "100%";
    video.style.display = "block";
    video.style.objectFit = "contain";
    video.style.borderRadius = "8px";

    // Create custom play button
    const playButton = document.createElement("div");
    playButton.style.position = "absolute";
    playButton.style.top = "50%";
    playButton.style.left = "50%";
    playButton.style.transform = "translate(-50%, -50%)";
    playButton.style.width = "60px";
    playButton.style.height = "60px";
    playButton.style.backgroundColor = "#56FEFF";
    playButton.style.borderRadius = "50%";
    playButton.style.cursor = "pointer";
    playButton.style.display = "flex";
    playButton.style.alignItems = "center";
    playButton.style.justifyContent = "center";
    playButton.style.transition = "all 0.3s ease";
    playButton.style.boxShadow = "0 4px 15px rgba(0, 0, 0, 0.3)";
    playButton.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style="margin-left: 1px;">
        <path d="M8 5v14l11-7z" fill="#ffff"/>
      </svg>
    `;

    playButton.addEventListener("mouseenter", () => {
      playButton.style.backgroundColor = "#6DFFFF";
      playButton.style.transform = "translate(-50%, -50%) scale(1.1)";
    });
    playButton.addEventListener("mouseleave", () => {
      playButton.style.backgroundColor = "#56FEFF";
      playButton.style.transform = "translate(-50%, -50%) scale(1)";
    });

    playButton.addEventListener("click", () => {
      if (video.paused) {
        video.play();
        playButton.style.display = "none";
      }
    });

    video.addEventListener("click", () => {
      if (!video.paused) {
        video.pause();
        playButton.style.display = "flex";
      }
    });

    video.addEventListener("play", () => {
      playButton.style.display = "none";
    });

    video.addEventListener("pause", () => {
      playButton.style.display = "flex";
    });

    video.addEventListener("ended", () => {
      playButton.style.display = "flex";
    });

    videoWrapper.appendChild(video);
    videoWrapper.appendChild(playButton);
    container.appendChild(videoWrapper);
    videoElement = video;
  }

  // Comment image
  if (commentImageUrl) {
    const commentImg = document.createElement("img");
    commentImg.src = commentImageUrl;
    commentImg.style.position = "absolute";
    commentImg.style.top = commentImageTop;
    commentImg.style.left = commentImageLeft;
    commentImg.style.transform = "translateX(-50%)";
    if (commentImageWidth) commentImg.style.width = commentImageWidth;
    commentImg.style.height = commentImageHeight ?? "auto";
    commentImg.style.pointerEvents = "none";
    commentImg.style.zIndex = "1";
    container.appendChild(commentImg);
  }

  // Navigation Text Row
  const navRow = document.createElement("div");
  navRow.style.display = "flex";
  navRow.style.justifyContent = "space-between";
  navRow.style.width = "100%";
  navRow.style.color = "#fff";
  navRow.style.fontSize = "14px";
  navRow.style.fontWeight = "600";
  navRow.style.marginBottom = "0px";
  navRow.style.paddingLeft = "150px";
  navRow.style.paddingRight = "150px";
  navRow.style.boxSizing = "border-box";

  // Left Text (Back)
  const leftText = document.createElement("span");
  leftText.textContent = backText;
  leftText.style.cursor = "pointer";

  // Right Text (Continue)
  const rightText = document.createElement("span");
  rightText.textContent = forwardText;
  rightText.style.cursor = "pointer";

  navRow.appendChild(leftText);
  navRow.appendChild(rightText);

  // Add nav to container, and container to overlay
  container.appendChild(navRow);
  overlay.appendChild(container);

  // --- Logic & Cleanup ---
  const cleanup = () => {
    try {
      if (overlay && overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
      document.removeEventListener("keydown", handleKeyPress);
    } catch (e) {
      console.warn("Cleanup error in instructionVideo:", e);
    }
  };

  const finishAndContinue = () => {
    cleanup();
    if (typeof onContinue === "function") onContinue();
  };

  const handleKeyPress = (event) => {
    const key = event.key.toLowerCase();

    // play video: Key "R" - restart and play video
    if (key === "r" && videoElement) {
      event.preventDefault();
      videoElement.currentTime = 0;
      videoElement.play();
      return;
    }

    // Continue: Key "/" — first press arms + plays video, second press advances
    if (key === "/" || event.code === "Slash" || event.code === "NumpadDivide") {
      event.preventDefault();
      if (videoElement && !slashArmed) {
        slashArmed = true;
        if (videoElement.paused) videoElement.play();
        return;
      }
      finishAndContinue();
    }
  };

  document.addEventListener("keydown", handleKeyPress);

  // attach cleanup to element just in case
  overlay.__cleanup = cleanup;

  return overlay;
}
